import * as React from "react";
import type { SAScenario } from "@/data/sellerAnalysisTypes";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  scenarios: SAScenario[];
}

export const SA_Scenarios: React.FC<Props> = ({ scenarios }) => {
  if (!scenarios?.length) return null;

  return (
    <section
      className="w-full bg-ivory py-20 md:py-28"
      data-testid="sa-scenarios"
      aria-label="Three possible outcomes"
    >
      <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
        <Reveal>
          <Overline>Three possible outcomes</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            Three ways I'd think about the sale.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {scenarios.map((s, i) => {
            const emphasized = s.key === "target";
            return (
              <Reveal key={s.key} delay={i * 0.08}>
                <div
                  className={`flex h-full flex-col p-7 md:p-8 ${
                    emphasized
                      ? "bg-charcoal text-ivory"
                      : "border border-charcoal/12 bg-ivory-surface text-charcoal"
                  }`}
                  data-testid={`scenario-${s.key}`}
                >
                  <p
                    className={`text-[0.62rem] uppercase tracking-[0.22em] ${
                      emphasized ? "text-brass" : "text-charcoal-muted"
                    }`}
                  >
                    {s.title}
                  </p>
                  <p
                    className={`mt-4 font-serif text-2xl leading-tight md:text-[1.7rem] ${
                      emphasized ? "text-ivory" : "text-charcoal"
                    }`}
                  >
                    {s.priceLabel}
                  </p>
                  {s.netLabel && (
                    <p
                      className={`mt-2 text-sm ${
                        emphasized ? "text-ivory/70" : "text-charcoal-muted"
                      }`}
                    >
                      {s.netLabel}
                    </p>
                  )}

                  <div
                    className={`mt-6 space-y-4 border-t pt-6 text-sm leading-relaxed ${
                      emphasized ? "border-ivory/15" : "border-charcoal/10"
                    }`}
                  >
                    <div>
                      <p
                        className={`text-[0.6rem] uppercase tracking-[0.16em] ${
                          emphasized ? "text-ivory/50" : "text-charcoal-muted"
                        }`}
                      >
                        What it takes
                      </p>
                      <p className={`mt-1 ${emphasized ? "text-ivory/85" : "text-charcoal-soft"}`}>
                        {s.whatItTakes}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`text-[0.6rem] uppercase tracking-[0.16em] ${
                          emphasized ? "text-ivory/50" : "text-charcoal-muted"
                        }`}
                      >
                        Trade-off
                      </p>
                      <p className={`mt-1 ${emphasized ? "text-ivory/85" : "text-charcoal-soft"}`}>
                        {s.tradeoff}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
